if (!customElements.get('archive-bundle-offer')) {
    customElements.define('archive-bundle-offer', class extends HTMLElement {
        static pending = false
        static uncertain = false

        connectedCallback() {
            this.controller?.abort()
            this.controller = new AbortController()
            this.addEventListener('change', () => this.update(), { signal: this.controller.signal })
            this.querySelector('[data-archive-add]').addEventListener('click', () => this.add(), { signal: this.controller.signal })
            this.update()
        }

        disconnectedCallback() {
            this.controller?.abort()
        }

        selections() {
            return [...this.querySelectorAll('[data-archive-choice]')].map(select => ({
                select,
                option: select.selectedOptions[0],
                handle: select.closest('[data-handle]').dataset.handle
            }))
        }

        update() {
            const choices = this.selections()
            const total = choices.reduce((sum, choice) => sum + Number(choice.option?.dataset.price || 0), 0)
            choices.forEach(choice => {
                choice.select.closest('[data-handle]').querySelector('[data-archive-piece-price]').textContent = `${Shopify.formatMoney(Number(choice.option?.dataset.price || 0))} ${this.dataset.currency}`
            })
            const discount = this.dataset.ready === 'true' && this.dataset.complement !== 'true' && this.dataset.currency === 'USD' ? Number(this.dataset.discountAmount || 0) : 0
            this.querySelector('[data-archive-total]').textContent = `${Shopify.formatMoney(Math.max(0, total - discount))} ${this.dataset.currency}`
            const individual = this.querySelector('[data-archive-individual]')
            const saving = this.querySelector('[data-archive-saving]')
            const confirmation = this.querySelector('[data-archive-added]')
            if (individual) individual.textContent = `${Shopify.formatMoney(total)} ${this.dataset.currency}`
            if (saving) {
                saving.textContent = `SAVE ${Shopify.formatMoney(Math.min(total, discount))}`
                saving.hidden = discount <= 0
            }
            if (confirmation) confirmation.hidden = !this.querySelector('[data-archive-feedback]').textContent.startsWith('Added.')
            this.querySelector('[data-archive-add]').disabled = this.dataset.ready !== 'true' || this.busy || this.constructor.uncertain || choices.some(choice => !choice.option || choice.option.disabled || choice.option.dataset.available !== 'true')
        }

        async add() {
            const button = this.querySelector('[data-archive-add]')
            if (button.disabled || this.constructor.pending || this.constructor.uncertain || document.querySelector('.archive-product form.loading, #sticky-atc[data-archive="true"] form.loading')) return
            this.constructor.pending = true
            this.busy = true
            this.setAttribute('aria-busy', 'true')
            const label = button.textContent
            const feedback = this.querySelector('[data-archive-feedback]')
            const root = window.Shopify.routes?.root || '/'
            const requestId = crypto.randomUUID()
            let mutationStarted = false
            let mutationAcknowledged = false
            const choices = this.selections()
            choices.forEach(choice => { choice.select.disabled = true })
            button.textContent = 'ADDING...'
            button.setAttribute('aria-busy', 'true')
            feedback.textContent = ''
            this.update()
            try {
                const products = await Promise.all(choices.map(async choice => {
                    const response = await fetch(`${root}products/${encodeURIComponent(choice.handle)}.js`)
                    if (!response.ok) throw new Error('Availability could not be checked. Please try again.')
                    return response.json()
                }))
                for (const [index, choice] of choices.entries()) {
                    const variant = products[index].variants.find(candidate => String(candidate.id) === choice.option.value)
                    if (!variant?.available) {
                        choice.option.disabled = true
                        choice.option.dataset.available = 'false'
                        throw new Error('A selected piece is no longer available. Choose another variant or visit its stock notifications.')
                    }
                    if (variant.price !== Number(choice.option.dataset.price)) {
                        choice.option.dataset.price = variant.price
                        choice.option.textContent = variant.title
                        throw new Error('An item price changed. Review the updated selection before adding.')
                    }
                }
                const items = choices.map(choice => ({
                    id: Number(choice.option.value),
                    quantity: 1,
                    properties: { _archive_request: requestId, _archive_set: this.dataset.kind }
                }))
                if (new Set(items.map(item => item.id)).size !== items.length) throw new Error('Choose one variant for each included piece.')
                mutationStarted = true
                const response = await fetch(`${root}cart/add.js`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
                })
                mutationAcknowledged = true
                if (!response.ok) throw new Error('The complete selection could not be added. Please review availability.')
                const cartResponse = await fetch(`${root}cart.js`)
                if (!cartResponse.ok) throw new Error('Your cart could not be verified.')
                const cart = await cartResponse.json()
                const added = cart.items.filter(item => item.properties?._archive_request === requestId)
                if (added.length !== items.length || items.some(item => !added.some(line => line.variant_id === item.id && line.quantity === 1))) {
                    throw new Error('The complete selection could not be verified.')
                }
                if (this.dataset.complement !== 'true' && !cart.items.some(item => choices.some(choice => choice.handle === item.handle) && item.line_level_discount_allocations?.some(allocation => allocation.amount > 0 && allocation.discount_application.title === this.dataset.discountTitle))) {
                    throw new Error('The configured Shopify set discount was not applied. The set has not been kept in your cart.')
                }
                await window.refreshCartContents(response)
                feedback.textContent = 'Added. Current prices and Shopify discounts are shown in your cart.'
                if (document.querySelector('#offcanvas-cart')) window.bootstrap.Offcanvas.getOrCreateInstance('#offcanvas-cart').show()
            } catch (error) {
                let message = error.message
                if (mutationStarted) {
                    try {
                        const cartResponse = await fetch(`${root}cart.js`)
                        if (!cartResponse.ok) throw new Error('Cart lookup failed')
                        const cart = await cartResponse.json()
                        const updates = Object.fromEntries(cart.items.filter(item => item.properties?._archive_request === requestId).map(item => [item.key, 0]))
                        if (Object.keys(updates).length) {
                            const rollback = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) })
                            if (!rollback.ok) throw new Error('Cart recovery failed')
                        }
                        await window.refreshCartContents(await fetch(`${root}cart.js`))
                        if (!mutationAcknowledged) {
                            this.constructor.uncertain = true
                            message = 'The add request was interrupted. Review and reload your cart before trying again.'
                        }
                    } catch {
                        this.constructor.uncertain = true
                        message = 'Your cart could not be verified. Review your cart before trying again.'
                    }
                }
                feedback.textContent = message
                this.querySelector('[data-archive-review]').hidden = false
                document.querySelectorAll('archive-bundle-offer').forEach(offer => {
                    if (offer.dataset.offerId === this.dataset.offerId || this.constructor.uncertain) {
                        offer.querySelector('[data-archive-feedback]').textContent = message
                        offer.querySelector('[data-archive-review]').hidden = false
                        offer.update()
                    }
                })
                const alert = document.querySelector('#offcanvas-cart-alert-error')
                if (alert) {
                    alert.querySelector('span').textContent = message
                    alert.hidden = false
                }
            } finally {
                this.busy = false
                this.removeAttribute('aria-busy')
                this.constructor.pending = false
                choices.forEach(choice => { choice.select.disabled = false })
                button.textContent = label
                button.removeAttribute('aria-busy')
                this.update()
            }
        }
    })
}